import { Icon } from "@common/index";

interface QuantityStepperProps {
    value: number;
    onChange: (next: number) => void;
    min?: number;
    max?: number;
    // The sku can't be ordered: every control is disabled.
    disabled?: boolean;
    // The id of the element that says why (aria-describedby on each control).
    describedBy?: string;
}

export const QuantityStepper = ({
    value,
    onChange,
    min = 1,
    max,
    disabled = false,
    describedBy,
}: QuantityStepperProps) => {
    const clamp = (n: number) => {
        if (Number.isNaN(n)) return min;
        if (n < min) return min;
        if (max !== undefined && n > max) return max;
        return n;
    };

    const handleDecrease = () => onChange(clamp(value - 1));
    const handleIncrease = () => onChange(clamp(value + 1));

    return (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-base-300 bg-info/20 px-2 py-1">
            <button
                type="button"
                aria-label="Decrease quantity"
                aria-describedby={describedBy}
                onClick={handleDecrease}
                disabled={disabled || value <= min}
                className="btn btn-ghost btn-sm btn-square text-neutral"
            >
                <Icon name="remove" className="text-lg" />
            </button>
            <input
                type="number"
                role="spinbutton"
                aria-label="Quantity"
                aria-describedby={describedBy}
                disabled={disabled}
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(clamp(parseInt(e.target.value, 10)))}
                className="w-16 bg-transparent text-center font-semibold text-neutral focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <button
                type="button"
                aria-label="Increase quantity"
                aria-describedby={describedBy}
                onClick={handleIncrease}
                disabled={disabled || (max !== undefined && value >= max)}
                className="btn btn-ghost btn-sm btn-square text-neutral"
            >
                <Icon name="add" className="text-lg" />
            </button>
        </div>
    );
};
